import { useRef, useEffect, useState } from "react";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import adapt from "@turnaroundfactor/common";
import { v4 } from "uuid";
// @ts-ignore
import { useSession, useSettings, useRoom } from "@turnaroundfactor/react";

export const useTranslationStore = create((set, get) => ({
	messages: [],
	addMessage: (msg) => {
		const current = get().messages;
		if (current.find((s) => s.uuid === msg.uuid)) {
			return get().updateMessage(msg.uuid, msg);
		}

		set((prev) => ({ messages: [...prev.messages, msg] }));
		// adapt.room?.broadcast("user_event", { type: "TRANSLATION", payload: msg });
	},
	addSession: (session) => {
		const current = get().sessions;
		if (current.find((s) => s.uuid === session.uuid)) return;
		set({ sessions: [...current, session] });
	},
	clear: () => set({ messages: [] }),
	closeSession: (uuid) => set({ sessions: get().sessions.filter((s) => s.uuid !== uuid) }),
	speechConfig: null,
	sessions: [],
	setConfig: (speechConfig) => set({ speechConfig }),
	updateMessage: (uuid, msg) => {
		set((prev) => ({
			messages: prev.messages.map((m) => (m.uuid === uuid ? { ...m, ...msg } : m)),
		}));

		// adapt.room?.broadcast("user_event", { type: "TRANSLATION", payload: msg });
	},
}));

export function useTranslation() {
	const [session, setSession] = useState(null);
	const translation = useRef(null);

	const { user } = useSession();
	const { broadcast } = useRoom();

	const {
		update: updateSettings,
		settings: { sendTranslations },
	} = useSettings();

	const sessions = useTranslationStore((store) => store.sessions);
	const sessionList = useRef(sessions || []);

	const { speechConfig } = useTranslationStore(
		useShallow((store) => ({
			updateMessage: store.updateMessage,
			addMessage: store.addMessage,
			speechConfig: store.speechConfig,
		})),
	);

	function destroy() {
		if (session) {
			session.stopContinuousRecognitionAsync(
				() => {
					try {
						session.close();
						setSession(null);
					} catch (e) {}
				},
				() => {
					try {
						session.close();
						setSession(null);
					} catch (e) {}
				},
			);
		}
	}

	function start() {
		if (sendTranslations === false) return;
		console.log("[Translation] Starting local recognition");
		session?.startContinuousRecognitionAsync();
	}

	useEffect(() => {
		if (session) session.close();
		if (!speechConfig || !adapt.mediaStream) return;

		let audioConfig;

		try {
			audioConfig = sdk.AudioConfig.fromStreamInput(
				new MediaStream(
					adapt.mediaStream.getTracks(),
					// adapt.mediaStream.getTracks().map((t) => {
					// 	const repl = t.clone();
					// 	repl.enabled = true;
					// 	return repl;
					// })
				),
			);
		} catch (e) {
			console.error("[Translation] Unable to create an AudioConfig from stream input");
			return;
		}

		const autoDetectSpeechLanguageConfig = sdk.AutoDetectSourceLanguageConfig.fromOpenRange();
		const recognizer = sdk.TranslationRecognizer.FromConfig(speechConfig, autoDetectSpeechLanguageConfig, audioConfig);

		const template = {
			user: { name: user.name, uuid: user.uuid },
		};

		recognizer.canceled = (_, e) => {
			console.log("RECOGNIZER.CANCELED", e);
		};

		// recognizer.onConnection = () => {
		// 	console.log("RECOGNIZER.onCONNECTION");
		// };

		recognizer.sessionStarted = () => {
			console.log("[Translation] Local session successfully started");
		};

		recognizer.recognizing = (_, e) => {
			const langs = new Set(sessionList.current.map((sess) => sess.language));

			console.log("RECOGNIZER.RECOGNIZING");
			console.log(e.result.translations);

			langs.forEach((language) => {
				if (language === null) return;

				const translatedText = e.result.translations.get(language);
				const last = translation.current;

				console.log(`[Translation] Recognized partial text [${language}]:`);
				console.log(`[Translation] Body: "${translatedText}"`);

				if (!translatedText) return;

				const msg = {
					...template,
					body: translatedText,
					partial: true,
					language,
				};

				if (last) {
					broadcast("TRANSLATION", {
						uuid: last,
						...msg,
					});

					return;
				} else {
					const msgId = v4();
					translation.current = msgId;
					broadcast("TRANSLATION", {
						uuid: msgId,
						...msg,
						partial: true,
						datetime: new Date(),
					});
				}
			});
		};

		recognizer.recognized = (s, e) => {
			if (e.result.reason !== sdk.ResultReason.TranslatedSpeech) return;

			const langs = new Set(sessionList.current.map((sess) => sess.language));

			console.log("RECOGNIZER.RECOGNIZED");
			console.log(e.result.translations);

			langs.forEach((language) => {
				if (language === null) return;

				const translatedText = e.result.translations.get(language);
				const last = translation.current;

				console.log(`[Translation] Recognized text [${language}]:`);
				console.log(`[Translation] Body: "${translatedText}"`);

				if (!translatedText?.length) return;

				if (last) {
					broadcast("TRANSLATION", {
						uuid: last,
						...template,
						body: translatedText,
						partial: false,
						language,
					});

					translation.current = null;
					return;
				}

				broadcast("TRANSLATION", {
					uuid: v4(),
					...template,
					body: translatedText,
					partial: false,
					language,
					datetime: new Date(),
				});
			});
		};

		recognizer.synthesizing = (s, e) => {
			if (!e.result.audio) return;

			console.log("RECOGNIZER.SYNTHESIZING", e.result);
			const encoded = btoa(e.result.audio);

			broadcast("TRANSLATION_AUDIO", {
				...template,
				datetime: new Date(),
				data: encoded,
			});
			// const blob = new Blob([e.result.audio], { type: 'audio/wav'})
		};

		recognizer.sessionStopped = (s, e) => {
			console.log("[Translation] Local session successfully stopped");
		};

		setSession(recognizer);
		start();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [speechConfig]);

	useEffect(() => {
		console.log(`[Translation] Translation enabled: ${sendTranslations}`);

		if (sendTranslations === true && session && sessions.length > 0) {
			console.log("[Translation] Starting local recognition");
			session.startContinuousRecognitionAsync(
				() => {},
				(e) => console.warn("[Translation] Failed to start local translation", e),
			);
		} else if (session) {
			console.log("[Translation] Stop local recognition");
			session.stopContinuousRecognitionAsync(
				() => {},
				(e) => console.warn("[Translation] Failed to stop local translation", e),
			);
		}
	}, [sendTranslations, session, sessions]);

	useEffect(() => {
		return () => {
			destroy();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		console.log("[Translation] Configured local translation recognizer", {
			session,
		});
	}, [session]);

	useEffect(() => {
		console.log("[Translation] Session lists updated", sessions.length);
		sessionList.current = sessions;

		// if (session) {
		// 	const active = sessions.map((s) => s.language);
		// 	const current = session.targetLanguages || [];
		// 	console.log("languages", { active, current });

		// 	active.forEach((lang) => {
		// 		if (!current.includes(lang)) session.addTargetLanguage(lang);
		// 	});

		// 	current.forEach((lang) => {
		// 		if (!active.includes(lang)) session.removeTargetLanguage(lang);
		// 	});
		// }

		updateSettings({ sendTranslations: sessions.length > 0 });
		return () => updateSettings({ sendTranslations: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessions, session]);

	return {
		enabled: sendTranslations,
	};
}
